import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { api } from '@/api/client'
import type { ProblemCategory } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { HelpPanel } from '@/components/HelpPanel'
import { articleBySlug, articleForPath } from '@/help'
import { useCertAccess } from '@/lib/certs'
import { HelpContext } from '@/lib/help'

export interface NavLinkItem {
  to: string
  label: string
}

/** A dropdown of related screens. Used when a module has too many for one bar. */
export interface NavGroupItem {
  label: string
  items: NavLinkItem[]
}

export type NavItem = NavLinkItem | NavGroupItem

export type ModuleKey = 'jobs' | 'certs'

const PROBLEMS_PATH = '/office/problems'
const SAGE_GROUP = 'Sage'

function isGroup(i: NavItem): i is NavGroupItem {
  return 'items' in i
}

/** Is this link the current page, or (for a section's own pages) inside it?
 *  A module's home ('/office', '/field', '/certs') matches exactly only. */
function isCurrent(to: string, pathname: string): boolean {
  if (/^\/(office|field|certs)$/.test(to)) return pathname === to
  return pathname === to || pathname.startsWith(`${to}/`)
}

/**
 * Shared chrome for every experience.
 *
 * The `field-context` class is what re-scales the design system for gloved,
 * one-handed use — larger tap targets and type. Office routes keep the dense
 * defaults. One shell, two densities.
 *
 * MODULES. The portal holds more than one application: Job Management (time,
 * per diem, jobs, crews) and Certifications. Each has its own menu, so adding
 * a module never adds buttons to another's bar. The "Job Management" label
 * beside the wordmark becomes a switch when the user has more than one module
 * open to them; otherwise it reads exactly as it always has.
 */
export function AppShell({
  variant,
  nav,
  module = 'jobs',
  children,
}: {
  variant: 'field' | 'office'
  nav: NavItem[]
  module?: ModuleKey
  children?: ReactNode
}) {
  const { user, signOut, hasRole } = useAuth()
  const certs = useCertAccess()
  const { pathname } = useLocation()

  /**
   * Help. null = closed; '' = the article for this screen; otherwise a slug.
   * The ? key opens it from anywhere except while typing in a field, where ?
   * is just a question mark.
   */
  const [helpSlug, setHelpSlug] = useState<string | null>(null)
  const openHelp = useCallback((slug?: string) => setHelpSlug(slug ?? ''), [])
  const helpCtx = useMemo(() => ({ open: openHelp }), [openHelp])
  const helpArticle = helpSlug ? (articleBySlug(helpSlug) ?? articleForPath(pathname)) : articleForPath(pathname)

  useEffect(() => setHelpSlug(null), [pathname])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '?' || e.ctrlKey || e.metaKey || e.altKey) return
      const t = e.target as HTMLElement | null
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return
      e.preventDefault()
      setHelpSlug((s) => (s === null ? '' : null))
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  /**
   * Open problem count, on the Problems nav item.
   *
   * The Problems screen is pull-only — someone has to decide to open it. That
   * suits a job with the wrong per diem site, which waits patiently. It does
   * not suit the overtime check, which is time-boxed: once a week posts to
   * Sage its rows leave the query, so the signal vanishes exactly when it
   * stopped being actionable. A badge is what makes a count visible to
   * somebody who was not looking for it.
   */
  const [problems, setProblems] = useState<ProblemCategory[] | null>(null)

  useEffect(() => {
    // Office-only: the endpoint requires that role, and a supervisor has
    // nothing to do with any of these findings.
    if (variant !== 'office' || module !== 'jobs') return
    let cancelled = false
    void (async () => {
      try {
        const s = await api.problemsSummary()
        if (!cancelled) setProblems(s)
      } catch {
        // A badge is not worth an error state. If it cannot load, the nav
        // simply looks the way it did before.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [variant, module])

  /**
   * Refused Sage lines, on the Sage menu. Like the Problems badge, it is there
   * so a failure is seen by someone who was not looking for it. Re-read on
   * every page change: a retry or a fresh send clears it, and the call is cheap.
   */
  const [sageFailed, setSageFailed] = useState<{ count: number; title: string } | null>(null)

  useEffect(() => {
    if (variant !== 'office' || module !== 'jobs') return
    let cancelled = false
    void (async () => {
      try {
        const s = await api.sageBridge()
        if (cancelled) return
        const hit = s.filter((c) => c.needs_attention > 0)
        setSageFailed({
          count: hit.reduce((n, c) => n + c.needs_attention, 0),
          title: hit.map((c) => `${c.needs_attention} refused by Sage in ${c.company}`).join('\n'),
        })
      } catch {
        // No badge rather than an error.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [variant, module, pathname])

  const openProblems = (problems ?? []).reduce((n, c) => n + c.count, 0)
  const problemBreakdown = (problems ?? [])
    .filter((c) => c.count > 0)
    .map((c) => `${c.count} ${c.title.toLowerCase()}`)
    .join('\n')

  /**
   * Cross-link between the two halves of Job Management.
   *
   * Without this an Admin lands on /office and has no way to reach the field
   * app short of typing the URL — the two route trees were built as separate
   * experiences and nothing joined them.
   *
   * Gated on the exact role rather than the rank ladder: Office outranks
   * Supervisor numerically, but they are different jobs, not steps on a
   * ladder. Accounting staff have no business recording crew time.
   */
  const canUseField = user?.role === 'Admin' || user?.role === 'Supervisor'
  const canUseOffice = user?.role === 'Admin' || user?.role === 'Office'
  const crossLink =
    module !== 'jobs'
      ? null
      : variant === 'office' && canUseField
        ? { to: '/field', label: 'Record crew time' }
        : variant === 'field' && canUseOffice
          ? { to: '/office', label: 'Office' }
          : null

  // Which modules this person may enter. Job Management needs Supervisor
  // (field) or Office; Certifications needs the server's gate. A technician
  // has only Certifications, so sees no switch at all.
  const jobsHome = hasRole('Office') ? '/office' : hasRole('Supervisor') ? '/field' : null
  const modules: { key: ModuleKey; label: string; to: string }[] = []
  if (jobsHome) modules.push({ key: 'jobs', label: 'Job Management', to: jobsHome })
  if (certs.enabled && certs.can('view_own')) modules.push({ key: 'certs', label: 'Certifications', to: '/certs' })
  const current = modules.find((m) => m.key === module)

  return (
    <div className={`app-shell ${variant === 'field' ? 'field-context' : 'office-context'}`}>
      <header className="app-header">
        <h1 className="brand">
          <span className="brand-mark">NW Linings</span>
          <span className="brand-rule" aria-hidden />
          {modules.length > 1 ? (
            <ModuleSwitch modules={modules} current={module} />
          ) : (
            <span className="brand-sub">
              {current?.label ?? (module === 'certs' ? 'Certifications' : variant === 'field' ? 'Crew Time' : 'Job Management')}
            </span>
          )}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {crossLink && (
            <NavLink
              to={crossLink.to}
              style={{
                color: 'inherit',
                fontSize: '0.8125rem',
                padding: '0.25rem 0.75rem',
                border: '1px solid rgb(255 255 255 / 0.45)',
                borderRadius: 'var(--radius)',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {crossLink.label} →
            </NavLink>
          )}
          <button
            type="button"
            className="help-button"
            onClick={() => openHelp()}
            title="Help for this screen (?)"
            aria-label="Help for this screen"
          >
            ?
          </button>
          <span className="app-header-name" style={{ fontSize: '0.8125rem', opacity: 0.9 }}>
            {user?.display_name}
          </span>
          <button
            className="btn btn-secondary"
            style={{ minHeight: 36, padding: '0.25rem 0.75rem', fontSize: '0.8125rem' }}
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="app-main">
        {nav.length > 1 && (
          // Grouped bars do not scroll sideways: a scrolling container would
          // clip the dropdowns, and with groups there are few enough entries
          // to wrap instead.
          <nav className={`nav${nav.some(isGroup) ? ' nav-grouped' : ''}`}>
            {nav.map((item) =>
              isGroup(item) ? (
                <NavGroup
                  key={item.label}
                  group={item}
                  badge={item.label === SAGE_GROUP && sageFailed && sageFailed.count > 0 ? sageFailed : null}
                />
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end
                  className={({ isActive }) => (isActive ? 'active' : '')}
                >
                  {item.label}
                  {item.to === PROBLEMS_PATH && openProblems > 0 && (
                    <span className="nav-badge" title={problemBreakdown}>
                      {openProblems}
                    </span>
                  )}
                </NavLink>
              ),
            )}
          </nav>
        )}
        <HelpContext.Provider value={helpCtx}>{children ?? <Outlet />}</HelpContext.Provider>
      </main>
      {helpSlug !== null && <HelpPanel article={helpArticle} onClose={() => setHelpSlug(null)} />}
    </div>
  )
}

/** The module switch in the header: one segment per module open to the user. */
function ModuleSwitch({
  modules,
  current,
}: {
  modules: { key: ModuleKey; label: string; to: string }[]
  current: ModuleKey
}) {
  return (
    <nav className="module-switch" aria-label="Switch between Job Management and Certifications">
      {modules.map((m) => (
        <NavLink
          key={m.key}
          to={m.to}
          className={m.key === current ? 'active' : ''}
          aria-current={m.key === current ? 'page' : undefined}
        >
          {m.label}
        </NavLink>
      ))}
    </nav>
  )
}

/**
 * A dropdown section of the bar. Highlighted when any screen inside it is
 * open, so you can see where you are without opening it. Closes on a choice,
 * on Escape, on a click elsewhere, and whenever the route changes.
 */
function NavGroup({
  group,
  badge = null,
}: {
  group: NavGroupItem
  badge?: { count: number; title: string } | null
}) {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const active = group.items.some((i) => isCurrent(i.to, pathname))

  useEffect(() => setOpen(false), [pathname])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="nav-group" ref={ref}>
      <button
        type="button"
        className={active ? 'active' : ''}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {group.label} <span aria-hidden>▾</span>
        {badge && (
          <span className="nav-badge" title={badge.title}>
            {badge.count}
          </span>
        )}
      </button>
      {open && (
        <div className="nav-menu" role="menu">
          {group.items.map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              role="menuitem"
              end
              className={isCurrent(i.to, pathname) ? 'active' : ''}
            >
              {i.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}
